/**
 * Users list page with pagination and filtering
 */

import type { UserStatus } from "@eoln/userpool/types";
import { AlertCircle, Loader2, Plus, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "@/components/Pagination";
import { UserActionsMenu } from "@/components/UserActionsMenu";
import { UserStatusBadge } from "@/components/UserStatusBadge";
import {
  useActivateUser,
  useDeleteUser,
  useDisableUser,
  useEnableUser,
  useSuspendUser,
  useUnlockUser,
  useUnsuspendUser,
  useUsers,
} from "@/hooks/useUsers";

const ITEMS_PER_PAGE = 10;

const statusOptions: { value: UserStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "DISABLED", label: "Disabled" },
  { value: "LOCKED", label: "Locked" },
];

export function UsersList() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<UserStatus | "all">("all");

  const { data, isLoading, error } = useUsers({
    limit: ITEMS_PER_PAGE,
    offset: (page - 1) * ITEMS_PER_PAGE,
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const activateUser = useActivateUser();
  const suspendUser = useSuspendUser();
  const unsuspendUser = useUnsuspendUser();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const unlockUser = useUnlockUser();
  const deleteUser = useDeleteUser();

  const totalPages = data ? Math.ceil(data.totalCount / ITEMS_PER_PAGE) : 0;

  const handleStatusFilterChange = (newStatus: UserStatus | "all") => {
    setStatusFilter(newStatus);
    setPage(1); // Reset to first page when filter changes
  };

  const handleDelete = async (userId: string, email: string) => {
    if (window.confirm(`Are you sure you want to delete user ${email}?`)) {
      await deleteUser.mutateAsync(userId);
    }
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/30">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="ml-3 text-sm text-red-700 dark:text-red-300">
            {error instanceof Error ? error.message : "Failed to load users"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
        </div>
        <Link
          to="/users/new"
          className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create User
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value as UserStatus | "all")}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : data?.nodes.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {statusFilter === "all"
                ? "Get started by creating a new user."
                : `No users with status "${statusFilter}".`}
            </p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {data?.nodes.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="whitespace-nowrap px-6 py-4">
                      <Link
                        to={`/users/${user.userId}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {user.email}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {user.fullName || "-"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <UserStatusBadge status={user.status} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <UserActionsMenu
                        status={user.status}
                        onActivate={() => activateUser.mutate(user.userId)}
                        onSuspend={() => suspendUser.mutate({ userId: user.userId })}
                        onUnsuspend={() => unsuspendUser.mutate(user.userId)}
                        onDisable={() => disableUser.mutate({ userId: user.userId })}
                        onEnable={() => enableUser.mutate(user.userId)}
                        onUnlock={() => unlockUser.mutate(user.userId)}
                        onDelete={() => handleDelete(user.userId, user.email)}
                        isLoading={
                          activateUser.isPending ||
                          suspendUser.isPending ||
                          unsuspendUser.isPending ||
                          disableUser.isPending ||
                          enableUser.isPending ||
                          unlockUser.isPending ||
                          deleteUser.isPending
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={data?.totalCount || 0}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
